// src/admin/components/Dashboard/SalesDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  RiHandCoinLine, RiBuildingLine, RiAddLine, RiArrowRightLine,
  RiRefreshLine, RiEyeLine, RiTeamLine, RiCalendarLine,
  RiMoneyDollarCircleLine, RiFileList3Line
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';

const SalesDashboard = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState([]);

  const fetchData = async (showRefresh = false) => {
    try {
      showRefresh ? setRefreshing(true) : setLoading(true);
      const [statsRes, salesRes] = await Promise.all([
        api.get('/reports/dashboard-stats'),
        api.get('/sales').catch(() => ({ data: { sales: [] } }))
      ]);
      setStats(statsRes.data?.stats || {});
      const allSales = salesRes.data?.sales || salesRes.data || [];
      setSales(Array.isArray(allSales) ? allSales.slice(0, 6) : []);
    } catch (err) {
      console.error('Sales dashboard error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const card = 'rounded-xl border shadow-md ' + (isDarkMode ? 'bg-gray-850 border-gray-700' : 'bg-white border-gray-200');
  const textMuted = isDarkMode ? 'text-zinc-400' : 'text-gray-500';
  const textMain = isDarkMode ? 'text-white' : 'text-gray-900';

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

  const statCards = [
    {
      label: t('total_properties'),
      value: stats?.total_properties ?? '—',
      sub: 'Total showroom units',
      icon: RiBuildingLine,
      iconBg: 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
      path: '/admin/apartments'
    },
    {
      label: t('active_tenants'),
      value: stats?.active_tenants ?? '—',
      sub: `${stats?.occupancy_rate ?? 0}% ${t('occupancy_rate')}`,
      icon: RiTeamLine,
      iconBg: 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400',
      path: '/admin/tenants'
    },
    {
      label: t('sales') || 'Total Sales',
      value: sales.length.toString(),
      sub: 'Active contracts',
      icon: RiHandCoinLine,
      iconBg: 'bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
      path: '/admin/sales'
    },
    {
      label: t('pending_payments'),
      value: stats?.pending_payments_count ?? '0',
      sub: fmt(stats?.pending_payments_amount) + ' total pending',
      icon: RiMoneyDollarCircleLine,
      iconBg: 'bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
      path: '/admin/payments?status=pending'
    }
  ];

  const quickActions = [
    { label: t('new_sale') || 'New Sale', icon: RiAddLine, path: '/admin/sales/add', color: 'text-purple-500' },
    { label: t('sale_list') || 'Sale List', icon: RiFileList3Line, path: '/admin/sales', color: 'text-green-500' },
    { label: t('apartments'), icon: RiBuildingLine, path: '/admin/apartments', color: 'text-blue-500' },
    { label: t('owners'), icon: RiTeamLine, path: '/admin/owners', color: 'text-orange-500' },
  ];

  const getInstallmentStatusColor = (status) => {
    const map = {
      paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      overdue: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };
    return map[status] || map.pending;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className={`${card} p-6 animate-pulse`}>
            <div className="h-6 bg-gray-200 dark:bg-zinc-800 rounded w-1/3 mb-3" />
            <div className="h-8 bg-gray-200 dark:bg-zinc-800 rounded w-1/4" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`${card} p-6`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${textMain}`}>
              {t('welcome_back')}, {user?.firstName}! 🤝
            </h1>
            <p className={`mt-1 ${textMuted}`}>Track your sales pipeline and showroom availability.</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/admin/sales/add')}
              className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <RiAddLine size={16} /> {t('new_sale') || 'New Sale'}
            </button>
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-900 hover:bg-zinc-800 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'} transition-colors`}
            >
              <RiRefreshLine className={refreshing ? 'animate-spin' : ''} size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s) => (
          <div
            key={s.label}
            onClick={() => navigate(s.path)}
            className={`${card} p-5 cursor-pointer hover:shadow-lg transition-shadow`}
          >
            <div className="flex items-center justify-between mb-3">
              <p className={`text-sm font-medium ${textMuted}`}>{s.label}</p>
              <div className={`p-2 rounded-full ${s.iconBg}`}>
                <s.icon size={20} />
              </div>
            </div>
            <p className={`text-3xl font-bold ${textMain}`}>{s.value}</p>
            {s.sub && <p className={`text-xs mt-1 ${textMuted}`}>{s.sub}</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Sales */}
        <div className={`lg:col-span-2 ${card}`}>
          <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className={`font-semibold ${textMain} flex items-center gap-2`}>
              <RiHandCoinLine className="text-purple-500" />
              {t('sale_list') || 'Recent Sales'}
            </h3>
            <button onClick={() => navigate('/admin/sales')} className="text-sm text-red-500 hover:text-red-400 flex items-center gap-1">
              {t('view_all')} <RiArrowRightLine size={14} />
            </button>
          </div>
          <div className="p-5">
            {sales.length === 0 ? (
              <div className="text-center py-10">
                <RiHandCoinLine className={`mx-auto mb-3 ${textMuted}`} size={40} />
                <p className={`text-sm ${textMuted}`}>No sales yet. Start by creating a new sale.</p>
                <button
                  onClick={() => navigate('/admin/sales/add')}
                  className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors"
                >
                  + {t('new_sale') || 'New Sale'}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {sales.map((sale, i) => (
                  <div
                    key={sale.id || i}
                    onClick={() => navigate(`/admin/sales/${sale.id}`)}
                    className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all ${
                      isDarkMode
                        ? 'border-gray-700 hover:border-purple-500 hover:bg-purple-900/10'
                        : 'border-gray-200 hover:border-purple-400 hover:bg-purple-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                        <RiHandCoinLine size={18} />
                      </div>
                      <div>
                        <p className={`text-sm font-semibold ${textMain}`}>
                          {sale.buyerName || sale.owner_name || `Sale #${sale.id}`}
                        </p>
                        <p className={`text-xs ${textMuted}`}>
                          Unit: {sale.apartmentNumber || sale.apartment_number || '—'}
                        </p>
                        {sale.nextPaymentDate && sale.paymentStatus !== 'paid' && (() => {
                          const nd = new Date(sale.nextPaymentDate);
                          const now = new Date(); now.setHours(0,0,0,0);
                          const overdue = nd < now;
                          return (
                            <p className={`text-xs mt-0.5 flex items-center gap-1 ${overdue ? 'text-red-400' : 'text-blue-400'}`}>
                              <RiCalendarLine size={11} />
                              Next: {nd.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                              {sale.nextPaymentAmount > 0 && <span>· {fmt(sale.nextPaymentAmount)}</span>}
                              {overdue && <span className="font-semibold"> (overdue)</span>}
                            </p>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="text-end">
                      <p className={`text-sm font-bold ${textMain}`}>
                        {fmt(sale.totalPrice || sale.total_price || 0)}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getInstallmentStatusColor(sale.paymentStatus || sale.status || 'pending')}`}>
                        {sale.paymentStatus || sale.status || 'pending'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-4">
          <div className={card}>
            <div className="p-5 border-b border-gray-200 dark:border-gray-700">
              <h3 className={`font-semibold ${textMain}`}>{t('quick_actions')}</h3>
            </div>
            <div className="p-5 space-y-2">
              {quickActions.map((a) => (
                <button
                  key={a.label}
                  onClick={() => navigate(a.path)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                    isDarkMode
                      ? 'hover:bg-zinc-900 border border-gray-700'
                      : 'hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  <a.icon className={a.color} size={18} />
                  <span className={`text-sm font-medium ${textMain}`}>{a.label}</span>
                  <RiArrowRightLine className={`ml-auto ${textMuted}`} size={14} />
                </button>
              ))}
            </div>
          </div>

          {/* Availability Snapshot */}
          <div className={card}>
            <div className="p-5 border-b border-gray-200 dark:border-gray-700">
              <h3 className={`font-semibold ${textMain} flex items-center gap-2`}>
                <RiBuildingLine className="text-blue-500" /> Availability
              </h3>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex justify-between items-center">
                <span className={`text-sm ${textMuted}`}>Total Units</span>
                <span className={`text-sm font-bold ${textMain}`}>{stats?.total_properties ?? '—'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className={`text-sm ${textMuted}`}>Occupied</span>
                <span className="text-sm font-bold text-green-500">{stats?.active_tenants ?? '—'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className={`text-sm ${textMuted}`}>Occupancy</span>
                <span className="text-sm font-bold text-purple-500">{stats?.occupancy_rate ?? 0}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-zinc-800 rounded-full h-2 mt-2">
                <div
                  className="bg-gradient-to-r from-purple-500 to-green-500 h-2 rounded-full transition-all"
                  style={{ width: `${stats?.occupancy_rate || 0}%` }}
                />
              </div>
              <button
                onClick={() => navigate('/admin/apartments')}
                className="w-full mt-2 text-sm text-center text-red-500 hover:text-red-400 flex items-center justify-center gap-1"
              >
                <RiEyeLine size={14} /> View Available Units
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;
